/**
 * 界 テストページ用データ
 * - 多言語対応（ja, en, ko, zh_cn, zh_tw）
 * - PC/SP 画像切り替え（wide/narrow）
 */

import { AvailableLanguage } from '~/schemes/language';

// ========================================
// 型定義
// ========================================

export type { AvailableLanguage };
export type LanguageMap<T> = Record<AvailableLanguage, T>;

export interface DeviceAsset {
  wide: string;
  narrow: string;
}

// ========================================
// 多言語テキスト
// ========================================

/** 構造化データ用: slogan */
export const BRAND_SLOGAN: LanguageMap<string> = {
  ja: '30の温泉宿 30の温泉文化、ご当地につかろう',
  en: "30 Onsen Ryokans 30 Onsen Cultures, Dip into what's local",
  ko: '30 Onsen Ryokans 30 Onsen Cultures, 이 고장에 푹 빠지자',
  zh_cn: '30 Onsen Ryokans 30 Onsen Cultures, 沉浸当地',
  zh_tw: '30 Onsen Ryokans 30 Onsen Cultures, 沉浸當地',
};

/** 構造化データ用: description */
export const BRAND_DESCRIPTION: LanguageMap<string> = {
  ja:
    '全国に展開する温泉旅館ブランド「界」。30の温泉宿を通じて「ご当地につかろう」をテーマに、温泉と地域の文化を満喫する旅を提案。',
  en:
    "A nationwide brand of hot spring ryokan \"KAI\". Centered around the theme 'Dip into what's local,' KAI offers journeys to fully enjoy hot springs and regional cultures through its 30 ryokans.",
  ko:
    "'계(KAI)'는 일본 전국에 전개하는 온천 여관 브랜드입니다. '이 고장에 푹 빠지자'를 테마로, 30개 온천 여관을 통해 온천과 지역 문화를 만끽하는 여행을 제안합니다.",
  zh_cn:
    '"界"是遍布日本全国的温泉旅馆品牌。以"沉浸当地"为主题，通过30家温泉旅馆，为您提供充分享受温泉与当地文化的旅途。',
  zh_tw:
    '「界」是遍及日本全國的溫泉旅館品牌。以「沉浸當地」為主題，透過30家溫泉旅館，為您提供充分享受溫泉與當地文化的旅程。',
};

/** セクションタイトル（プロパティごとに多言語） */
const SECTION_TITLES: {
  featureMain: LanguageMap<string[]>;
  newHotels: LanguageMap<string>;
  filterLabel: LanguageMap<string>;
} = {
  featureMain: {
    ja: ['界がもてなす', '温泉文化体験'],
    en: ['Onsen culture at KAI'],
    ko: ['카이가 제공하는', '온천 문화 체험'],
    zh_cn: ['"界"之款待', '温泉文化体验'],
    zh_tw: ['界之款待', '溫泉文化體驗'],
  },
  newHotels: {
    ja: '新施設のご紹介',
    en: 'New Locations',
    ko: '신규 시설 소개',
    zh_cn: '新设施介绍',
    zh_tw: '新設施介紹',
  },
  filterLabel: {
    ja: '何につかる？',
    en: 'What do you want to dip into?',
    ko: '무엇에 빠지고 싶으세요?',
    zh_cn: '想沉浸于什么？',
    zh_tw: '沉浸於什麼之中？',
  },
};

/** Feature タイトル（縦書き） */
export const FEATURE_TITLES: LanguageMap<string[]> = {
  ja: ['味わう', '感じる', '学ぶ', '触れる', '浸かる'],
  en: ['EAT', 'FEEL', 'LEARN', 'DO', 'RELAX'],
  ko: ['맛보다', '느끼다', '배우다', '체험하다', '휴식하다'],
  zh_cn: ['品味', '感受', '趣学', '体验', '放松'],
  zh_tw: ['品味', '感受', '樂學', '接觸', '放鬆'],
};

/** Feature テキスト（アイテムごとに多言語） */
type FeatureTextItem = {
  title: LanguageMap<string>;
  sub: LanguageMap<string>;
};

const FEATURE_TEXTS: FeatureTextItem[] = [
  {
    title: {
      ja: '日本旅会席',
      en: 'Kaiseki',
      ko: '가이세키 요리',
      zh_cn: '会席料理',
      zh_tw: '會席料理',
    },
    sub: {
      ja: 'その土地を、味わい尽くす。',
      en: 'Indulging in the flavors of the land',
      ko: '이 지역의 음식을 마음껏 맛보세요.',
      zh_cn: '尽情品味当地美食。',
      zh_tw: '盡情品味這片土地的美食。',
    },
  },
  {
    title: {
      ja: 'ご当地部屋',
      en: 'KAI Signature Rooms',
      ko: '시그니처룸',
      zh_cn: '当地特色客房',
      zh_tw: '當地特色客房',
    },
    sub: {
      ja: '泊まるだけで、その土地がわかる。',
      en: 'Diving deeper into the local character',
      ko: '머무는 것만으로 이 지역을 느껴 보세요.',
      zh_cn: '入住其中，便能感受当地风土。',
      zh_tw: '只要住宿於此，就能感受這片土地。',
    },
  },
  {
    title: {
      ja: 'ご当地楽',
      en: 'KAI Cultural Discovery',
      ko: 'KAI Cultural Discovery',
      zh_cn: '当地乐',
      zh_tw: '當地樂',
    },
    sub: {
      ja: 'その土地の"芸"に出会う。',
      en: "Encountering the area's artistry",
      ko: '이 지역의 "예술"을 만나보세요.',
      zh_cn: '邂逅当地独有的"艺术"。',
      zh_tw: '邂逅這片土地的「技藝」。',
    },
  },
  {
    title: {
      ja: '手業のひととき',
      en: 'A Moment of Craftsmanship',
      ko: '손재주가 돋보이는 한때',
      zh_cn: '手作技艺时光',
      zh_tw: '手工藝的片刻',
    },
    sub: {
      ja: 'つくる時間が旅になる。',
      en: 'Embarking on creative journeys',
      ko: '만드는 시간마저 여행이 된다.',
      zh_cn: '动手创作，让时光成为旅程。',
      zh_tw: '製作的時光幻化旅程。',
    },
  },
  {
    title: {
      ja: 'ご当地湯治',
      en: 'Onsen',
      ko: '온천',
      zh_cn: '温泉',
      zh_tw: '溫泉',
    },
    sub: {
      ja: '湯につかり、わたしを癒す。',
      en: 'Recharging through hot spring dips',
      ko: '온천에 몸을 담그고 나를 치유하자.',
      zh_cn: '浸泡温泉，疗愈自我。',
      zh_tw: '沉浸溫泉，療癒自我。',
    },
  },
];

/** フィルターカテゴリ（label のみ多言語、hotels は ALL_HOTELS から自動導出） */
type FilterCategoryItem = {
  value: FilterCategory;
  label: LanguageMap<string>;
};

const FILTER_CATEGORIES: FilterCategoryItem[] = [
  {
    value: 'scenery',
    label: {
      ja: '「時の移ろいと絶景」につかる',
      en: 'Stunning views and dynamic scenery',
      ko: '"시간의 변화와 절경"에 빠지고 싶을 때',
      zh_cn: '沉浸于"时光流转与绝景"',
      zh_tw: '沉浸於「時光流轉與絕美景緻」',
    },
  },
  {
    value: 'nature',
    label: {
      ja: '「自然と静けさ」につかる',
      en: 'Nature and seclusion',
      ko: '"자연과 고요함"에 빠지고 싶을 때',
      zh_cn: '沉浸于"自然与静谧"',
      zh_tw: '沉浸於「大自然與靜謐」',
    },
  },
  {
    value: 'craft',
    label: {
      ja: '「手仕事とデザイン」につかる',
      en: 'Handicrafts and design',
      ko: '"향토 공예와 디자인"에 빠지고 싶을 때',
      zh_cn: '沉浸于"手工技艺与设计"',
      zh_tw: '沉浸於「手作與設計」',
    },
  },
  {
    value: 'gourmet',
    label: {
      ja: '「風土の恵みと味わい」につかる',
      en: 'Flavors and specialties',
      ko: '"풍토의 은총과 맛"에 빠지고 싶을 때',
      zh_cn: '沉浸于"风土馈赠与美味"',
      zh_tw: '沉浸於「風土的恩惠與滋味」',
    },
  },
  {
    value: 'town',
    label: {
      ja: '「湯の街と風情」につかる',
      en: 'History and locality',
      ko: '"온천 거리와 정취"에 빠지고 싶을 때',
      zh_cn: '沉浸于"温泉街与风情"',
      zh_tw: '沉浸於「溫泉小鎮與風情」',
    },
  },
];

/** リード文 */
export type LeadTextDevice = {
  wide: string[];
  narrow: string[];
};

export const LEAD_TEXT: LanguageMap<LeadTextDevice> = {
  ja: {
    wide: [
      '温泉が湧く。そこに人が集い、宿が建ち、食が育つ。\n工芸が磨かれ、芸能が生まれる。',
      '日本に根づいてきた、温泉を中心としたひとつの文化圏。\nその重なりを、私たちは「界」と呼ぶ。',
      '湯に浸かって身体を癒やし、文化に浸かって心を満たす。\nひとつの宿で味わう、その土地ならではのおもてなし。',
      'そんな旅に心惹かれたら、\n「界」に足を踏み入れてみてください。',
    ],
    narrow: [
      '温泉が湧く。\nそこに人が集い、宿が建ち、食が育つ。\n工芸が磨かれ、芸能が生まれる。',
      '日本に根づいてきた、\n温泉を中心としたひとつの文化圏。\nその重なりを、私たちは「界」と呼ぶ。',
      '湯に浸かって身体を癒やし、\n文化に浸かって心を満たす。\nひとつの宿で味わう、\nその土地ならではのおもてなし。',
      'そんな旅に心惹かれたら、\n「界」に足を踏み入れてみてください。',
    ],
  },
  en: {
    wide: [
      "Japan's onsen history belongs to its local communities—\npeople who shaped the spaces, food, crafts, and art.",
      'Each is a singular cultural sphere—\na world within the world of KAI.',
      'Dip into the bath to refresh,\nand the local culture for spiritual growth.',
      'Step into KAI to experience\ndistinctively local hospitality.',
    ],
    narrow: [
      "Japan's onsen history belongs\nto its local communities—\npeople who shaped the spaces,\nfood, crafts, and art.",
      'Each is a singular cultural\nsphere—a world within\nthe world of KAI.',
      'Dip into the bath to refresh,\nand the local\nculture for spiritual growth.',
      'Step into KAI to experience\ndistinctively local hospitality.',
    ],
  },
  ko: {
    wide: [
      '온천수가 솟는 곳에 사람이 모이면\n숙소가 세워지고 음식문화가 발달한다.\n공예가 발전하고 예능이 탄생한다.',
      '일본 각 지역에 뿌리내린 온천을\n중심으로 형성된 하나의 문화권.\n이러한 문화권들을 우리는 "카이"라고 부른다.',
      '온천수에 몸을 담가 몸의 피로를 치유하고,\n문화를 즐기며 마음을 채운다.\n한 곳의 숙소에서 맛보는 그 지역만의\n환대와 서비스.',
      '이런 여행이 끌린다면,\n"카이" 안으로 한발 들어서 보세요.',
    ],
    narrow: [
      '온천수가 솟는 곳에 사람이 모이면\n숙소가 세워지고 음식문화가 발달한다.\n공예가 발전하고 예능이 탄생한다.',
      '일본 각 지역에 뿌리내린 온천을\n중심으로 형성된 하나의 문화권.\n이러한 문화권들을\n우리는 "카이"라고 부른다.',
      '온천수에 몸을 담가\n몸의 피로를 치유하고,\n문화를 즐기며 마음을 채운다.\n한 곳의 숙소에서 맛보는\n그 지역만의 환대와 서비스.',
      '이런 여행이 끌린다면,\n"카이" 안으로 한발 들어서 보세요.',
    ],
  },
  zh_cn: {
    wide: [
      '温泉涌出，人们汇聚于此，\n旅馆由此而建，美食也孕育而生。\n工艺技艺不断精进，传统艺能也由此诞生。',
      '这是一个扎根于日本、\n以温泉为中心形成的文化圈。\n这片文化交融之境，就是我们的品牌"界"。',
      '泡温泉疗愈身心，沉浸文化滋养心灵。\n在一间旅馆里，体验当地独有的待客之道。',
      '如果您向往这样的旅程，\n不妨走进"界"，亲身体验一番。',
    ],
    narrow: [
      '温泉涌出，人们汇聚于此，\n旅馆由此而建，美食也孕育而生。\n工艺技艺不断精进，\n传统艺能也由此诞生。',
      '这是一个扎根于日本、\n以温泉为中心形成的文化圈。\n这片文化交融之境，\n就是我们的品牌"界"。',
      '泡温泉疗愈身心，沉浸文化滋养心灵。\n在一间旅馆里，体验当地独有的待客之道。',
      '如果您向往这样的旅程，\n不妨走进"界"，亲身体验一番。',
    ],
  },
  zh_tw: {
    wide: [
      '溫泉湧出之處，人們聚集，\n興建旅宿，栽培食材。\n琢磨工藝，孕育表演。',
      '扎根日本，以溫泉為中心形成的文化圈。\n我們將其疊合稱呼為「界」。',
      '浸泡溫泉，療癒身體；沉浸文化，滿足心靈。\n可在一間旅宿品味，這片土地特有的款待之道。',
      '如果您也嚮往這樣的旅程，\n請邁步踏入「界」。',
    ],
    narrow: [
      '溫泉湧出之處，人們聚集，\n興建旅宿，栽培食材。\n琢磨工藝，孕育表演。',
      '扎根日本，\n以溫泉為中心形成的文化圈。\n我們將其疊合稱呼為「界」。',
      '浸泡溫泉，療癒身體；\n沉浸文化，滿足心靈。\n可在一間旅宿品味，\n這片土地特有的款待之道。',
      '如果您也嚮往這樣的旅程，\n請邁步踏入「界」。',
    ],
  },
};

// ========================================
// 施設データ
// ========================================

/** テキストの型定義（jaのみPC/SP分離） */
export interface HotelText {
  ja: { pc: string; sp: string };
  en: string;
  ko: string;
  zh_cn: string;
  zh_tw: string;
}

/**
 * フィルターカテゴリの値
 * - `scenery`: 「時の移ろいと絶景」につかる
 * - `nature`: 「自然と静けさ」につかる
 * - `craft`: 「手仕事とデザイン」につかる
 * - `gourmet`: 「風土の恵みと味わい」につかる
 * - `town`: 「湯の街と風情」につかる
 */
export type FilterCategory =
  | 'scenery'
  | 'nature'
  | 'craft'
  | 'gourmet'
  | 'town';

/** ホテルデータの型定義 */
export interface HotelData {
  name: LanguageMap<string>;
  location: LanguageMap<string>;
  text: HotelText;
  /** 温泉名（日本語以外で表示、英語のみ） */
  onsenName?: string;
  /** 新施設かどうか */
  isNew?: boolean;
  /** 所属カテゴリ */
  categories?: FilterCategory[];
}

/** 全施設データ */
export const ALL_HOTELS: Record<string, HotelData> = {
  kaiporoto: {
    name: {
      ja: '界 ポロト',
      en: 'KAI Poroto',
      ko: 'KAI 포로토',
      zh_cn: 'KAI 波罗多',
      zh_tw: 'KAI 波羅多',
    },
    location: {
      ja: '北海道',
      en: 'Hokkaido',
      ko: '홋카이도',
      zh_cn: '北海道',
      zh_tw: '北海道',
    },
    text: {
      ja: {
        pc: 'ポロト湖畔\nとんがり湯小屋の宿で過ごす',
        sp: 'ポロト湖畔\nとんがり湯小屋の\n宿で過ごす',
      },
      en: '',
      ko: '',
      zh_cn: '',
      zh_tw: '',
    },
    onsenName: 'Hokkaido Shiraoi Onsen',
    categories: ['scenery'],
  },
  kaitsugaru: {
    name: {
      ja: '界 津軽',
      en: 'KAI Tsugaru',
      ko: 'KAI 쓰가루',
      zh_cn: 'KAI 津轻',
      zh_tw: 'KAI 津輕',
    },
    location: {
      ja: '青森',
      en: 'Aomori',
      ko: '아오모리',
      zh_cn: '青森',
      zh_tw: '青森',
    },
    text: {
      ja: {
        pc: '津軽に息づく\n文化と四季を巡る宿',
        sp: '津軽に息づく\n文化と四季を巡る宿',
      },
      en: '',
      ko: '',
      zh_cn: '',
      zh_tw: '',
    },
    onsenName: 'Aomori Owani Onsen',
    categories: ['gourmet'],
  },
  kaiakiu: {
    name: {
      ja: '界 秋保',
      en: 'KAI Akiu',
      ko: 'KAI 아키우',
      zh_cn: 'KAI 秋保',
      zh_tw: 'KAI 秋保',
    },
    location: {
      ja: '宮城',
      en: 'Miyagi',
      ko: '미야기',
      zh_cn: '宫城',
      zh_tw: '宮城',
    },
    text: {
      ja: {
        pc: '彩りの渓流で\n伊達なひととき',
        sp: '彩りの渓流で\n伊達なひととき',
      },
      en: '',
      ko: '',
      zh_cn: '',
      zh_tw: '',
    },
    onsenName: 'Miyagi Akiu Onsen',
    categories: ['nature'],
  },
  kaizaou: {
    name: {
      ja: '界 蔵王',
      en: 'KAI Zao',
      ko: 'KAI 자오',
      zh_cn: 'KAI 藏王',
      zh_tw: 'KAI 藏王',
    },
    location: {
      ja: '山形',
      en: 'Yamagata',
      ko: '야마가타',
      zh_cn: '山形',
      zh_tw: '山形',
    },
    text: {
      ja: {
        pc: '御釜で整う、\n温泉スパイラル',
        sp: '御釜で整う、\n温泉スパイラル',
      },
      en: '',
      ko: '',
      zh_cn: '',
      zh_tw: '',
    },
    onsenName: 'Yamagata Zao Onsen',
    isNew: true,
    categories: ['nature'],
  },
  kaikinugawa: {
    name: {
      ja: '界 鬼怒川',
      en: 'KAI Kinugawa',
      ko: 'KAI 기누가와',
      zh_cn: 'KAI 鬼怒川',
      zh_tw: 'KAI 鬼怒川',
    },
    location: {
      ja: '栃木',
      en: 'Tochigi',
      ko: '도치기',
      zh_cn: '栃木',
      zh_tw: '栃木',
    },
    text: {
      ja: {
        pc: 'モダンなとちぎ民藝と\n木漏れ日の中庭',
        sp: 'モダンな\nとちぎ民藝と\n木漏れ日の中庭',
      },
      en: '',
      ko: '',
      zh_cn: '',
      zh_tw: '',
    },
    onsenName: 'Tochigi Kinugawa Onsen',
    categories: ['craft'],
  },
  kaikusatsu: {
    name: {
      ja: '界 草津',
      en: 'KAI Kusatsu',
      ko: 'KAI 쿠사츠',
      zh_cn: 'KAI 草津',
      zh_tw: 'KAI 草津',
    },
    location: {
      ja: '群馬',
      en: 'Gunma',
      ko: '군마',
      zh_cn: '群马',
      zh_tw: '群馬',
    },
    text: {
      ja: {
        pc: 'トンネルがつなぐ\n木立の湯宿と温泉街',
        sp: 'トンネルがつなぐ\n木立の湯宿と温泉街',
      },
      en: '',
      ko: '',
      zh_cn: '',
      zh_tw: '',
    },
    onsenName: 'Gunma Kusatsu Onsen',
    isNew: true,
    categories: ['town'],
  },
  kaihakone: {
    name: {
      ja: '界 箱根',
      en: 'KAI Hakone',
      ko: 'KAI 하코네',
      zh_cn: 'KAI 箱根',
      zh_tw: 'KAI 箱根',
    },
    location: {
      ja: '神奈川',
      en: 'Kanagawa',
      ko: '가나가와',
      zh_cn: '神奈川',
      zh_tw: '神奈川',
    },
    text: {
      ja: {
        pc: '東海道の歴史に浸る\n箱根ごこちの湯宿',
        sp: '東海道の歴史に浸る\n箱根ごこちの湯宿',
      },
      en: '',
      ko: '',
      zh_cn: '',
      zh_tw: '',
    },
    onsenName: 'Kanagawa Hakone\nYumoto Onsen',
    categories: ['nature'],
  },
  kaisengokuhara: {
    name: {
      ja: '界 仙石原',
      en: 'KAI Sengokuhara',
      ko: 'KAI 센고쿠하라',
      zh_cn: 'KAI 仙石原',
      zh_tw: 'KAI 仙石原',
    },
    location: {
      ja: '神奈川',
      en: 'Kanagawa',
      ko: '가나가와',
      zh_cn: '神奈川',
      zh_tw: '神奈川',
    },
    text: {
      ja: {
        pc: 'アート心が湧きたつ\nアトリエ温泉旅館',
        sp: 'アート心が湧きたつ\nアトリエ温泉旅館',
      },
      en: '',
      ko: '',
      zh_cn: '',
      zh_tw: '',
    },
    onsenName: 'Kanagawa\nSengokuhara Onsen',
    categories: ['craft'],
  },
  kaianjin: {
    name: {
      ja: '界 アンジン',
      en: 'KAI Anjin',
      ko: 'KAI 안진',
      zh_cn: 'KAI Anjin',
      zh_tw: 'KAI Anjin',
    },
    location: {
      ja: '静岡',
      en: 'Shizuoka',
      ko: '시즈오카',
      zh_cn: '静冈',
      zh_tw: '靜岡',
    },
    text: {
      ja: {
        pc: '大航海時代の\nロマン漂う宿',
        sp: '大航海時代の\nロマン漂う宿',
      },
      en: '',
      ko: '',
      zh_cn: '',
      zh_tw: '',
    },
    onsenName: 'Shizuoka Ito Onsen',
    categories: ['scenery'],
  },
  kaiito: {
    name: {
      ja: '界 伊東',
      en: 'KAI Ito',
      ko: 'KAI 이토',
      zh_cn: 'KAI 伊东',
      zh_tw: 'KAI 伊東',
    },
    location: {
      ja: '静岡',
      en: 'Shizuoka',
      ko: '시즈오카',
      zh_cn: '静冈',
      zh_tw: '靜岡',
    },
    text: {
      ja: {
        pc: '温泉づくしに浸り、\n花暦を追う',
        sp: '温泉づくしに浸り、\n花暦を追う',
      },
      en: '',
      ko: '',
      zh_cn: '',
      zh_tw: '',
    },
    onsenName: 'Shizuoka Ito Onsen',
    categories: ['gourmet'],
  },
  kaienshu: {
    name: {
      ja: '界 遠州',
      en: 'KAI Enshu',
      ko: 'KAI 엔슈',
      zh_cn: 'KAI 远州',
      zh_tw: 'KAI 遠州',
    },
    location: {
      ja: '静岡',
      en: 'Shizuoka',
      ko: '시즈오카',
      zh_cn: '静冈',
      zh_tw: '靜岡',
    },
    text: {
      ja: {
        pc: '浜名湖を臨み\n煎茶に満たされる',
        sp: '浜名湖を臨み\n煎茶に満たされる',
      },
      en: '',
      ko: '',
      zh_cn: '',
      zh_tw: '',
    },
    onsenName: 'Shizuoka Kanzanji Onsen',
    categories: ['gourmet'],
  },
  kaialps: {
    name: {
      ja: '界 アルプス',
      en: 'KAI Alps',
      ko: 'KAI 알프스',
      zh_cn: 'KAI 阿尔卑斯',
      zh_tw: 'KAI 阿爾卑斯',
    },
    location: {
      ja: '長野',
      en: 'Nagano',
      ko: '나가노',
      zh_cn: '长野',
      zh_tw: '長野',
    },
    text: {
      ja: {
        pc: '信州の贅沢な\n田舎を体感する',
        sp: '信州の贅沢な\n田舎を体感する',
      },
      en: '',
      ko: '',
      zh_cn: '',
      zh_tw: '',
    },
    onsenName: 'Nagano\nHakuba/Omachi Onsen',
    categories: ['nature'],
  },
  kaimatsumoto: {
    name: {
      ja: '界 松本',
      en: 'KAI Matsumoto',
      ko: 'KAI 마츠모토',
      zh_cn: 'KAI 松本',
      zh_tw: 'KAI 松本',
    },
    location: {
      ja: '長野',
      en: 'Nagano',
      ko: '나가노',
      zh_cn: '长野',
      zh_tw: '長野',
    },
    text: {
      ja: {
        pc: '松本エレガンテに浸る湯宿',
        sp: '松本エレガンテに\n浸る湯宿',
      },
      en: '',
      ko: '',
      zh_cn: '',
      zh_tw: '',
    },
    onsenName: 'Nagano Asama Onsen',
    isNew: true,
    categories: ['gourmet'],
  },
  kaiokuhida: {
    name: {
      ja: '界 奥飛騨',
      en: 'KAI Okuhida',
      ko: 'KAI 오쿠히다',
      zh_cn: 'KAI 奥飞驒',
      zh_tw: 'KAI 奧飛驒',
    },
    location: {
      ja: '岐阜',
      en: 'Gifu',
      ko: '기후',
      zh_cn: '岐阜',
      zh_tw: '岐阜',
    },
    text: {
      ja: {
        pc: '山岳温泉にめざめ\n飛騨デザインに寛ぐ宿',
        sp: '山岳温泉にめざめ\n飛騨デザインに\n寛ぐ宿',
      },
      en: '',
      ko: '',
      zh_cn: '',
      zh_tw: '',
    },
    onsenName: 'Gifu Okuhida Onsen',
    categories: ['craft'],
  },
  kaikaga: {
    name: {
      ja: '界 加賀',
      en: 'KAI Kaga',
      ko: 'KAI 가가',
      zh_cn: 'KAI 加贺',
      zh_tw: 'KAI 加賀',
    },
    location: {
      ja: '石川',
      en: 'Ishikawa',
      ko: '이시카와',
      zh_cn: '石川',
      zh_tw: '石川',
    },
    text: {
      ja: {
        pc: '加賀の伝統を\n新たな感性でもてなす',
        sp: '加賀の伝統を\n新たな感性で\nもてなす',
      },
      en: '',
      ko: '',
      zh_cn: '',
      zh_tw: '',
    },
    onsenName: 'Ishikawa Yamashiro Onsen',
    categories: ['craft'],
  },
  kaitamatsukuri: {
    name: {
      ja: '界 玉造',
      en: 'KAI Tamatsukuri',
      ko: 'KAI 다마쓰쿠리',
      zh_cn: 'KAI 玉造',
      zh_tw: 'KAI 玉造',
    },
    location: {
      ja: '島根',
      en: 'Shimane',
      ko: '시마네',
      zh_cn: '岛根',
      zh_tw: '島根',
    },
    text: {
      ja: {
        pc: 'いにしえの湯と\n出雲文化を遊ぶ宿',
        sp: 'いにしえの湯と\n出雲文化を遊ぶ宿',
      },
      en: '',
      ko: '',
      zh_cn: '',
      zh_tw: '',
    },
    onsenName: 'Shimane Tamatsukuri Onsen',
    categories: ['gourmet'],
  },
  kaiizumo: {
    name: {
      ja: '界 出雲',
      en: 'KAI Izumo',
      ko: 'KAI 이즈모',
      zh_cn: 'KAI 出云',
      zh_tw: 'KAI 出雲',
    },
    location: {
      ja: '島根',
      en: 'Shimane',
      ko: '시마네',
      zh_cn: '岛根',
      zh_tw: '島根',
    },
    text: {
      ja: {
        pc: '灯台と水平線を望む\nお詣り支度の宿',
        sp: '灯台と水平線を望む\nお詣り支度の宿',
      },
      en: '',
      ko: '',
      zh_cn: '',
      zh_tw: '',
    },
    onsenName: 'Shimane Izumo\nHinomisaki Onsen',
    categories: ['scenery'],
  },
  kaimiyajima: {
    name: {
      ja: '界 宮島',
      en: 'KAI Miyajima',
      ko: 'KAI 미야지마',
      zh_cn: 'KAI 宫岛',
      zh_tw: 'KAI 宮島',
    },
    location: {
      ja: '広島',
      en: 'Hiroshima',
      ko: '히로시마',
      zh_cn: '广岛',
      zh_tw: '廣島',
    },
    text: {
      ja: {
        pc: '見晴らしの湯で、\nいつくしみの凪逗留',
        sp: '見晴らしの湯で、\nいつくしみの凪逗留',
      },
      en: '',
      ko: '',
      zh_cn: '',
      zh_tw: '',
    },
    onsenName: 'Hiroshima\nMiyajimaguchi Onsen',
    isNew: true,
    categories: ['scenery'],
  },
  kainagato: {
    name: {
      ja: '界 長門',
      en: 'KAI Nagato',
      ko: 'KAI 나가토',
      zh_cn: 'KAI 长门',
      zh_tw: 'KAI 長門',
    },
    location: {
      ja: '山口',
      en: 'Yamaguchi',
      ko: '야마구치',
      zh_cn: '山口',
      zh_tw: '山口',
    },
    text: {
      ja: {
        pc: '屋敷門を抜け\n温泉街をそぞろ歩く宿',
        sp: '屋敷門を抜け\n温泉街を\nそぞろ歩く宿',
      },
      en: '',
      ko: '',
      zh_cn: '',
      zh_tw: '',
    },
    onsenName: 'Yamaguchi Nagato\nYumoto Onsen',
    categories: ['town'],
  },
  kaibeppu: {
    name: {
      ja: '界 別府',
      en: 'KAI Beppu',
      ko: 'KAI 벳푸',
      zh_cn: 'KAI 别府',
      zh_tw: 'KAI 別府',
    },
    location: {
      ja: '大分',
      en: 'Oita',
      ko: '오이타',
      zh_cn: '大分',
      zh_tw: '大分',
    },
    text: {
      ja: {
        pc: 'ドラマティック\n温泉街に逗留する',
        sp: 'ドラマティック\n温泉街に逗留する',
      },
      en: '',
      ko: '',
      zh_cn: '',
      zh_tw: '',
    },
    onsenName: 'Oita Beppu Onsen',
    categories: ['town'],
  },
  kaiyufuin: {
    name: {
      ja: '界 由布院',
      en: 'KAI Yufuin',
      ko: 'KAI 유후인',
      zh_cn: 'KAI 由布院',
      zh_tw: 'KAI 由布院',
    },
    location: {
      ja: '大分',
      en: 'Oita',
      ko: '오이타',
      zh_cn: '大分',
      zh_tw: '大分',
    },
    text: {
      ja: { pc: '棚田暦で憩う宿', sp: '棚田暦で憩う宿' },
      en: '',
      ko: '',
      zh_cn: '',
      zh_tw: '',
    },
    onsenName: 'Oita Yufuin Onsen',
    categories: ['nature'],
  },
  kaiunzen: {
    name: {
      ja: '界 雲仙',
      en: 'KAI Unzen',
      ko: 'KAI 운젠',
      zh_cn: 'KAI 云仙',
      zh_tw: 'KAI 雲仙',
    },
    location: {
      ja: '長崎',
      en: 'Nagasaki',
      ko: '나가사키',
      zh_cn: '长崎',
      zh_tw: '長崎',
    },
    text: {
      ja: {
        pc: '地獄パワーにふれる、\n異国情緒の宿',
        sp: '地獄パワーに\nふれる、\n異国情緒の宿',
      },
      en: '',
      ko: '',
      zh_cn: '',
      zh_tw: '',
    },
    onsenName: 'Nagasaki Unzen Onsen',
    categories: ['town'],
  },
  kaikirishima: {
    name: {
      ja: '界 霧島',
      en: 'KAI Kirishima',
      ko: 'KAI 기리시마',
      zh_cn: 'KAI 雾岛',
      zh_tw: 'KAI 霧島',
    },
    location: {
      ja: '鹿児島',
      en: 'Kagoshima',
      ko: '가고시마',
      zh_cn: '鹿儿岛',
      zh_tw: '鹿兒島',
    },
    text: {
      ja: {
        pc: '桜島をはるかに見渡す\n絶景の湯宿',
        sp: '桜島を\nはるかに見渡す\n絶景の湯宿',
      },
      en: '',
      ko: '',
      zh_cn: '',
      zh_tw: '',
    },
    onsenName: 'Kagoshima\nKirishima Onsen',
    categories: ['scenery'],
  },
};

/** スライダー表示用 slug 一覧 */
export const HOTEL_SLUGS = Object.keys(ALL_HOTELS);

/** 新施設の slug（isNew: true のホテルを自動抽出） */
export const NEW_HOTEL_SLUGS = Object.entries(ALL_HOTELS)
  .filter(([, hotel]) => hotel.isNew)
  .map(([slug]) => slug);

/**
 * 指定カテゴリに属するホテルの slug 一覧を取得
 */
export function getHotelsByCategory(category: FilterCategory): string[] {
  return Object.entries(ALL_HOTELS)
    .filter(
      ([, hotel]) => hotel.categories && hotel.categories.includes(category),
    )
    .map(([slug]) => slug);
}

/** Feature アイテム数 */
export const FEATURE_COUNT = 5;

// ========================================
// 画像パス
// ========================================

const BASE_PATH = '/brands/kai/new';

/**
 * Feature セクション画像（言語別）
 * - ルート: デフォルト（ja）画像
 * - 言語フォルダ: 上書き用画像（en/, ko/, zh_cn/, zh_tw/）
 */
export const FEATURE_IMAGES: LanguageMap<string>[] = [
  // 1: 味わう / EAT
  {
    ja: `${BASE_PATH}/feature/feature-img-1.png`,
    en: `${BASE_PATH}/feature/en/feature-img-1.png`,
    ko: `${BASE_PATH}/feature/feature-img-1.png`,
    zh_cn: `${BASE_PATH}/feature/feature-img-1.png`,
    zh_tw: `${BASE_PATH}/feature/feature-img-1.png`,
  },
  // 2: 感じる / FEEL
  {
    ja: `${BASE_PATH}/feature/feature-img-2.png`,
    en: `${BASE_PATH}/feature/feature-img-2.png`,
    ko: `${BASE_PATH}/feature/feature-img-2.png`,
    zh_cn: `${BASE_PATH}/feature/zh_cn/feature-img-2.png`,
    zh_tw: `${BASE_PATH}/feature/feature-img-2.png`,
  },
  // 3: 学ぶ / LEARN
  {
    ja: `${BASE_PATH}/feature/feature-img-3.png`,
    en: `${BASE_PATH}/feature/feature-img-3.png`,
    ko: `${BASE_PATH}/feature/feature-img-3.png`,
    zh_cn: `${BASE_PATH}/feature/feature-img-3.png`,
    zh_tw: `${BASE_PATH}/feature/zh_tw/feature-img-3.png`,
  },
  // 4: 触れる / DO
  {
    ja: `${BASE_PATH}/feature/feature-img-4.png`,
    en: `${BASE_PATH}/feature/feature-img-4.png`,
    ko: `${BASE_PATH}/feature/feature-img-4.png`,
    zh_cn: `${BASE_PATH}/feature/zh_cn/feature-img-4.png`,
    zh_tw: `${BASE_PATH}/feature/feature-img-4.png`,
  },
  // 5: 浸かる / RELAX
  {
    ja: `${BASE_PATH}/feature/feature-img-5.png`,
    en: `${BASE_PATH}/feature/en/feature-img-5.png`,
    ko: `${BASE_PATH}/feature/ko/feature-img-5.png`,
    zh_cn: `${BASE_PATH}/feature/zh_cn/feature-img-5.png`,
    zh_tw: `${BASE_PATH}/feature/zh_tw/feature-img-5.png`,
  },
];

/** KV セクション画像 */
export const KV_IMAGES: {
  background: string;
  logo: LanguageMap<string>;
} = {
  background: `${BASE_PATH}/kv-background.png`,
  logo: {
    ja: `${BASE_PATH}/kv-logo-ja.svg`,
    en: `${BASE_PATH}/kv-logo-en.svg`,
    ko: `${BASE_PATH}/kv-logo-en.svg`,
    zh_cn: `${BASE_PATH}/kv-logo-en.svg`,
    zh_tw: `${BASE_PATH}/kv-logo-en.svg`,
  },
};

/** Lead セクション画像（全言語共通） */
export const LEAD_IMAGES: {
  background: DeviceAsset;
  green: string;
  yuge: DeviceAsset;
} = {
  background: {
    wide: `${BASE_PATH}/lead-background-pc.jpg`,
    narrow: `${BASE_PATH}/lead-background-sp.jpg`,
  },
  green: `${BASE_PATH}/lead-background-green.svg`,
  yuge: {
    wide: `${BASE_PATH}/lead-background-yuge-pc.png`,
    narrow: `${BASE_PATH}/lead-background-yuge-sp.png`,
  },
};

/** Hotels セクション画像（background は共通、title は多言語） */
export const HOTELS_IMAGES: {
  background: DeviceAsset;
  title: LanguageMap<string>;
} = {
  background: {
    wide: `${BASE_PATH}/hotels-background-pc.png`,
    narrow: `${BASE_PATH}/hotels-background-sp.png`,
  },
  title: {
    ja: `${BASE_PATH}/hotels-slide-title-ja.svg`,
    en: `${BASE_PATH}/hotels-slide-title-en.svg`,
    ko: `${BASE_PATH}/hotels-slide-title-en.svg`,
    zh_cn: `${BASE_PATH}/hotels-slide-title-en.svg`,
    zh_tw: `${BASE_PATH}/hotels-slide-title-en.svg`,
  },
};

/** New Hotels セクション画像（全言語共通） */
export const NEW_HOTELS_IMAGES = {
  background: {
    wide: `${BASE_PATH}/new-hotels-background-pc.png`,
    narrow: `${BASE_PATH}/new-hotels-background-sp.png`,
  },
};

// ========================================
// ヘルパー関数
// ========================================

/**
 * デバイスに応じたアセットパスを取得（片方あれば片方を表示）
 */
export function getDeviceAsset(asset: DeviceAsset, isNarrow: boolean): string {
  if (isNarrow) {
    return asset.narrow || asset.wide;
  }
  return asset.wide || asset.narrow;
}

/**
 * 現在の言語・デバイスに応じたリード文を取得
 */
export function getLeadText(
  lang: AvailableLanguage,
  isNarrow: boolean,
): string[] {
  const text = LEAD_TEXT[lang];
  if (isNarrow) {
    return text.narrow.some((t) => t) ? text.narrow : text.wide;
  }
  return text.wide.some((t) => t) ? text.wide : text.narrow;
}

/**
 * 言語に応じたセクションタイトルを取得
 */
export function getSectionTitles(lang: AvailableLanguage) {
  return {
    featureMain: SECTION_TITLES.featureMain[lang],
    newHotels: SECTION_TITLES.newHotels[lang],
    filterLabel: SECTION_TITLES.filterLabel[lang],
  };
}

/**
 * 言語に応じた Feature タイトルを取得
 */
export function getFeatureTitles(lang: AvailableLanguage): string[] {
  return FEATURE_TITLES[lang];
}

/**
 * 言語に応じた Feature テキストを取得
 */
export function getFeatureTexts(
  lang: AvailableLanguage,
): { title: string; sub: string }[] {
  return FEATURE_TEXTS.map((item) => ({
    title: item.title[lang],
    sub: item.sub[lang],
  }));
}

/**
 * 言語に応じた Feature 画像パスを取得
 */
export function getFeatureImages(lang: AvailableLanguage): string[] {
  return FEATURE_IMAGES.map((item) => item[lang]);
}

/**
 * 言語に応じたフィルターカテゴリを取得
 */
export function getFilterCategories(
  lang: AvailableLanguage,
): { value: FilterCategory; label: string; hotels: string[] }[] {
  return FILTER_CATEGORIES.map((item) => ({
    value: item.value,
    label: item.label[lang],
    hotels: getHotelsByCategory(item.value),
  }));
}

/**
 * ランダムなフィルターカテゴリを取得
 */
export function getRandomFilterCategory(): FilterCategory {
  const index = Math.floor(Math.random() * FILTER_CATEGORIES.length);
  return FILTER_CATEGORIES[index].value;
}

/**
 * 言語に応じたホテル名を取得
 */
export function getHotelName(slug: string, lang: AvailableLanguage): string {
  const hotel = ALL_HOTELS[slug];
  if (!hotel) return '';
  return hotel.name[lang];
}

/**
 * 言語に応じた全ホテルデータを取得
 */
export function getAllHotels(
  lang: AvailableLanguage,
): {
  slug: string;
  name: string;
  location: string;
  textPc: string;
  textSp: string;
  onsenName: string;
}[] {
  return Object.keys(ALL_HOTELS).map((slug) => {
    const hotel = ALL_HOTELS[slug];
    const hotelText = hotel.text;
    let textPc: string;
    let textSp: string;

    if (lang === 'ja') {
      textPc = hotelText.ja.pc;
      textSp = hotelText.ja.sp;
    } else {
      const text = hotelText[lang];
      textPc = text;
      textSp = text;
    }

    return {
      slug,
      name: hotel.name[lang],
      location: hotel.location[lang],
      textPc,
      textSp,
      onsenName: hotel.onsenName || '',
    };
  });
}

/**
 * 言語に応じた KV 画像を取得
 */
export function getKvImages(lang: AvailableLanguage) {
  return {
    background: KV_IMAGES.background,
    logo: KV_IMAGES.logo[lang],
  };
}

/**
 * 言語に応じた Lead 画像を取得
 */
export function getLeadImages() {
  return LEAD_IMAGES;
}

/**
 * 言語に応じた Hotels 画像を取得
 */
export function getHotelsImages(lang: AvailableLanguage) {
  return {
    background: HOTELS_IMAGES.background,
    title: HOTELS_IMAGES.title[lang],
  };
}

/**
 * 言語に応じた New Hotels 画像を取得
 */
export function getNewHotelsImages() {
  return NEW_HOTELS_IMAGES;
}
